import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DeskTable, ElementTable, ByView } from '../../components/nucleus/Shelf';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { SHELVES, shelfOf } from '../../components/nucleus/shelves';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { Cell } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import { ErrorEmpty } from '../../components/nucleus/Empty';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import { useOpen } from '../../components/nucleus/Inspector';
import { priceOf } from '../apps/appsData';
import PluginInspector from './PluginInspector';
import { usePluginsGate } from './gate';
import AutomationsView from './AutomationsView';
import RunsView from './RunsView';
import PluginTree from './PluginTree';
import { PluginCell, PageVerb, HeldLine } from './cells';
import { PluginStoreTable } from './PluginBoard';
import { pluginsApi } from './data/api';
import { usePluginList } from './data/hooks';
import { useLinkedPlugin } from './data/linked';
import { pluginStateWord, stateGroup, verifiedWord, MINE_STATES } from './data/words';
import { Plus, Code2 } from 'lucide-react';

// building your own: the developer portal, from the store
const DEVELOPER = 'https://developer.ofself.com';

// PLUGINS — what you set up and watch: My plugins (one list: Setting up,
// Ready, Off, each plugin with its tools under it), the Plugin store (the
// plugins not set up here), the Automations that run their tools, and their
// Runs. Everything a plugin needs is done in its inspector. THE CURRENT SPACE
// ONLY: in a group, the group's.
export default function PluginsSection() {
    const { page } = usePlace();
    if (page?.id === 'automations') return <AutomationsView />;
    if (page?.id === 'runs') return <RunsView />;
    return <PluginsPage store={page?.id === 'store'} />;
}

// THE FILTER RAILS. Mine: how each stands (Setting up · Ready · Off), and its
// category. The store: its category, whether ofSelf verified it, its price.
const SHELF_WORD = Object.fromEntries(SHELVES.map((s) => [s.id, s.label]));
const CATEGORY = { id: 'shelf', title: 'Category', of: (p) => shelfOf(p.plugin), order: SHELVES.map((s) => s.id), label: (k) => SHELF_WORD[k] || k };
const MINE_FACETS = [
    { id: 'state', title: 'State', of: (p) => stateGroup(p.state), order: MINE_STATES, label: (k) => pluginStateWord(k) },
    CATEGORY,
];
const STORE_FACETS = [
    CATEGORY,
    { id: 'verified', title: 'Verified', of: (p) => (p.verified ? 'yes' : 'no'), order: ['yes', 'no'], label: (k) => verifiedWord(k === 'yes') },
    { id: 'price', title: 'Price', of: (p) => (priceOf(p.plugin) ? 'paid' : 'free'), order: ['free', 'paid'], label: (k) => (k === 'paid' ? 'Paid' : 'Free') },
];
// the store finds by what a plugin is; mine by a plugin's name or one of its tools'
const storeText = (p) => `${p.name} ${p.blurb} ${p.madeBy || ''}`;
const mineText = (p) => [p.name, ...(p.tools || []).map((t) => t?.name), ...(p.plugin?.tools || []).map((t) => t?.name)].filter(Boolean).join(' ');

function PluginsPage({ store }) {
    const { groupId, section, page, go } = usePlace();
    const access = useSpaceAccess();
    const gate = usePluginsGate();
    const held = gate.isAdmin ? null : gate.hint;
    const toast = useToast();
    const list = usePluginList(groupId);
    const [openId, open] = useOpen();
    const [params, setParams] = useSearchParams();
    // an app that sent someone here (/plugins/<id>/setup?return_to=…) is gone
    // back to from the inspector, and forgotten once it closes
    const returnTo = params.get('return_to');
    const close = () => setParams((p) => { const n = new URLSearchParams(p); n.delete('open'); n.delete('return_to'); n.delete('tool'); n.delete('tab'); return n; });
    const linked = useLinkedPlugin(groupId, openId, list);
    // a door into the open plugin: a tool's sheet (&tool= &tab=) or its setup (&setup=first)
    const into = (id, keys) => setParams((p) => {
        const n = new URLSearchParams(p);
        ['tool', 'tab', 'setup'].forEach((k) => n.delete(k));
        n.set('open', String(id));
        Object.entries(keys).forEach(([k, v]) => { if (v != null) n.set(k, v); });
        return n;
    });

    // SET UP, from the store: it is installed quietly, and its setup opens on My plugins
    const setUp = async (p) => {
        try {
            await pluginsApi.install(p.id, groupId);
        } catch (e) {
            toast.error(e?.message || `Could not set up ${p.name}.`);
            return;
        }
        list.reload();
        go(null, `open=${encodeURIComponent(p.id)}&setup=first`);
    };
    const quick = (p) => [
        { id: 'open', label: `Open ${p.name}`, run: () => open(p.id) },
        !p.installed && { id: 'setup', label: 'Set up', primary: true, ...(held ? { disabled: true, hint: held } : {}), run: () => setUp(p) },
    ].filter(Boolean);

    const d = list.data;
    const loaded = !!d;
    const sets = !d ? [] : store
        ? [{ id: 'store', title: 'All plugins', list: d.store, empty: d.all.length ? 'You’ve set up every plugin here.' : 'No plugins in the store yet.' }]
        : [
            { id: 'setting', title: pluginStateWord('not_set_up'), list: d.settingUp },
            { id: 'ready', title: pluginStateWord('ready'), list: d.ready, empty: 'Nothing set up yet.' },
            { id: 'off', title: pluginStateWord('paused'), list: d.paused },
        ].filter((s) => s.list.length > 0 || (s.id === 'ready' && !d.installed.length));
    const total = d ? (store ? d.store.length : d.installed.length) : undefined;

    const f = useFacets(sets.flatMap((s) => s.list), store ? STORE_FACETS : MINE_FACETS, {
        text: store ? storeText : mineText, placeholder: store ? 'Find a plugin' : 'Find a plugin or tool',
    });
    const keep = new Set(f.shown);
    // each set as the filters leave it; a set the filters emptied steps aside
    const shown = sets
        .map((s) => ({ ...s, all: s.list.length, list: s.list.filter((p) => keep.has(p)) }))
        .filter((s) => !(f.filtering && !s.list.length && s.all));
    const emptyOf = (s) => (loaded ? (f.filtering ? 'Nothing matches the filters.' : s.empty) : undefined);
    const nothingHere = loaded && !store && !d.installed.length;

    const entry = linked.entry;
    const heldId = 'plugins-verb-held';
    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={total}
            verbs={store
                ? <Verb icon={<Code2 />} onClick={() => window.open(DEVELOPER, '_blank', 'noopener')}>Build a plugin</Verb>
                : <PageVerb primary icon={<Plus />} held={held} heldId={heldId} onClick={() => go('store')}>Get a plugin</PageVerb>}
            notice={(held && !store) || linked.notice ? (
                <>
                    {!store && <HeldLine id={heldId}>{held}</HeldLine>}
                    {linked.notice && <p className="plugins-held is-live" role="alert">{linked.notice.text}</p>}
                </>
            ) : null}
            inspector={entry && (
                <PluginInspector key={entry.id} item={entry} returnTo={returnTo} onClose={close} onChanged={list.reload}
                    onSetUp={store ? () => go(null, `open=${encodeURIComponent(entry.id)}`) : undefined} />
            )}
        >
            {list.error && !d
                ? <ErrorEmpty what={store ? 'the store' : groupId ? 'the group’s plugins' : 'your plugins'} error={list.error} retry={list.reload} />
                : (
                    <Faceted id={store ? 'plugins-store' : 'plugins'} rail={f.rail}>
                        {store ? (
                            <ByView
                                cards={(
                                    <DeskTable top={0} between={40}>
                                        {shown.map((s) => (
                                            <ElementTable key={s.id} title={s.title} count={loaded ? s.list.length : undefined} empty={emptyOf(s)}>
                                                {s.list.map((p) => (
                                                    <Cell key={p.id} actions={quick(p)}>
                                                        <PluginCell item={p} inStore current={String(p.id) === String(openId)} onPress={() => open(p.id)}
                                                            onSetUp={() => setUp(p)} held={held} />
                                                    </Cell>
                                                ))}
                                            </ElementTable>
                                        ))}
                                    </DeskTable>
                                )}
                                rows={(
                                    <DeskTable top={0} between={40}>
                                        {shown.map((s) => (
                                            <TitledSet key={s.id} title={s.title} count={loaded ? s.list.length : undefined} empty={emptyOf(s)}>
                                                {s.list.length > 0 && (
                                                    <PluginStoreTable items={s.list} openId={openId} onOpen={open} actionsOf={quick} onSetUp={setUp} held={held} />
                                                )}
                                            </TitledSet>
                                        ))}
                                    </DeskTable>
                                )}
                            />
                        ) : (
                            <PluginTree
                                sets={shown} query={f.search} openId={openId} loaded={loaded} emptyOf={emptyOf}
                                gate={gate} groupId={groupId} heldId={held ? heldId : undefined}
                                runHint={!access.runPlugins ? access.pluginHint : undefined}
                                onOpen={open}
                                onOpenTool={(pid, tid, tab) => into(pid, { tool: tid ?? '', tab: tab === 'schedule' ? 'schedule' : null })}
                                onContinue={(p) => into(p.id, { setup: 'first' })}
                                onChanged={list.reload}
                            />
                        )}
                        {nothingHere && !f.filtering && (
                            <p className="plugins-door">
                                <button type="button" className="nx-link" onClick={() => go('store')}>Open the Plugin store</button>
                            </p>
                        )}
                    </Faceted>
                )}
        </NucleusPage>
    );
}
